import React, { useEffect, useMemo, useState } from "react";
import { usePlayer } from "../../../../Contexts/playerData.context";
import HWSEditShow from "./addHWSData.component";
import HWSDraftScout from "./HWSDraftScout.component";
import DragBox2026 from "../../../Universals/DragBox/DragBox2026/dragBox2026.component.jsx";

const BOX_WIDTH = 1160;
const STORAGE_KEY_POS = "HD_BOX_LOCATION";
const DEFAULT_POS = {
     x: typeof window !== "undefined" ? window.innerWidth - BOX_WIDTH : 970,
     y: 95,
};

const isRealValue = (value) => {
     if (value === null || value === undefined) return false;
     const v = String(value).trim();
     return v !== "" && v !== "null" && v !== "NULL" && v !== "-";
};

const cleanDate = (value) => {
     if (!value || value === "0000-00-00" || value === "null" || value === "NULL") return "-";
     return value;
};

const cleanValue = (value, fallback = "-") => {
     return isRealValue(value) ? value : fallback;
};

const getHeaderTone = (impnip) => {
     if (impnip === "N") return "alert-danger";
     if (impnip === "G") return "alert-success";
     if (impnip?.startsWith("V")) return "alert-brown";
     return "alert-navy";
};

const LeagueRow = ({
     tone = "alert-secondary",
     label,
     team,
     status,
     date,
     draftYear,
     draftTeam,
     draftRound,
     draftRoundPick,
     extraRight,
}) => {
     return (
          <div className={`col-48 ${tone} d-flex align-items-center px-2 py-2 mb-1 rounded`}>
               <div className="col-12 d-flex align-items-center">
                    <div className="col-14 text-end pe-2 fw-bold">{label}:</div>
                    <div className="col-14 text-truncate" title={cleanValue(team, "-")}>
                         {cleanValue(team, "-")}
                    </div>
               </div>

               <div className="col-4 text-center" title="Status">
                    {cleanValue(status, "NA")}
               </div>

               <div className="col-6 text-center" title="Date">
                    {cleanDate(date)}
               </div>

               <div className="col-4 text-center" title="Draft Year">
                    {cleanValue(draftYear, "-")}
               </div>

               <div className="col-6 text-center text-truncate" title={cleanValue(draftTeam, "-")}>
                    {cleanValue(draftTeam, "-")}
               </div>

               <div className="col-5 text-center" title="Draft Round">
                    {cleanValue(draftRound, "NA")}
               </div>

               <div className="col-5 text-center" title="Draft Pick">
                    {cleanValue(draftRoundPick, "NA")}
               </div>

               <div className="col-2 text-end text-truncate" title={extraRight || ""}>
                    {extraRight || ""}
               </div>
          </div>
     );
};

const DataRow = ({
     leftLabel,
     leftValue,
     leftSub,
     leftDate,
     rightLabel,
     rightValue,
     rightSub,
     rightDate,
}) => {
     return (
          <div className="col-48 d-flex align-items-center py-1 mb-1 border-bottom">
               <div className="col-6 text-end pe-2 fw-bold">{leftLabel}:</div>
               <div className="col-4">{cleanValue(leftValue, "NA")}</div>
               <div className="col-3">{cleanValue(leftSub, "-")}</div>
               <div className="col-7">{cleanDate(leftDate)}</div>

               <div className="col-6 text-end pe-2 fw-bold">{rightLabel}:</div>
               <div className="col-4">{cleanValue(rightValue, "NA")}</div>
               <div className="col-3">{cleanValue(rightSub, "-")}</div>
               <div className="col-7">{cleanDate(rightDate)}</div>
          </div>
     );
};

const SplitRow = ({
     leftLabel,
     leftValue,
     midLabel,
     midValue,
     rightLabel,
     rightValue,
}) => {
     return (
          <div className="col-48 d-flex align-items-center py-1 mb-1 border-bottom">
               <div className="col-8 text-end pe-2 fw-bold">{leftLabel}:</div>
               <div className="col-8 text-truncate" title={cleanValue(leftValue, "NA")}>
                    {cleanValue(leftValue, "NA")}
               </div>

               <div className="col-8 text-end pe-2 fw-bold">{midLabel}:</div>
               <div className="col-8 text-truncate" title={cleanValue(midValue, "NA")}>
                    {cleanValue(midValue, "NA")}
               </div>

               <div className="col-8 text-end pe-2 fw-bold">{rightLabel}:</div>
               <div className="col-8 text-truncate" title={cleanValue(rightValue, "NA")}>
                    {cleanValue(rightValue, "NA")}
               </div>
          </div>
     );
};

const HDBox = () => {
     const { activePlayer, playerData } = usePlayer();
     const [whatHDBox, setWhatHDBox] = useState("General");
     const [player, setPlayer] = useState({});

     const currentPlayer = useMemo(() => {
          const id = activePlayer?.FBXID;
          if (!id) return activePlayer || {};

          const arr = Array.isArray(playerData) ? playerData : [];
          const found = arr.find((p) => String(p?.FBXID) === String(id));
          return found || activePlayer || {};
     }, [activePlayer, playerData]);

     useEffect(() => {
          if (!currentPlayer) {
               setPlayer({});
               return;
          }

          const roles = {
               LS: currentPlayer.ls,
               PR: currentPlayer.pr,
               KR: currentPlayer.kr,
               ST: currentPlayer.st,
               G: currentPlayer.g,
               P: currentPlayer.p,
          };

          const specialTeams = Object.entries(roles)
               .filter(([_, value]) => String(value) === "1")
               .map(([key]) => key)
               .join(", ");

          setPlayer({
               ...currentPlayer,
               specialTeams: specialTeams || "No Special Teams",
          });
     }, [currentPlayer]);

     return (
          <DragBox2026
               storageKey={STORAGE_KEY_POS}
               defaultPos={DEFAULT_POS}
               title="Hard Data Box - "
               countText={
                    player?.last
                         ? `${player.last}, ${player.first} [${player.school || "-"}]`
                         : "No Player Selected"
               }
               minWidth={1150}
               maxWidth={1150}
               zIndex={500}
               showReset={true}
          >
               <div
                    id="hdbox"
                    className="text-uppercase fs-7"
                    style={{ width: "100%", minHeight: "200px" }}
               >
                    <div className={`col-48 d-flex align-items-center px-2 py-2 mb-2 rounded ${getHeaderTone(player.impnip)}`}>
                         <div className="col-14 fw-bold text-truncate" style={{ minWidth: "220px" }}>
                              <span className={player.impnip === "N" ? "text-danger" : ""}>
                                   {cleanValue(player.last, "")}
                                   {player.last ? ", " : ""}
                                   {cleanValue(player.first, "")}
                              </span>
                              {(player.impnip === "N" || player.impnip === "G" || player.impnip?.startsWith("V")) && (
                                   <span className={`ms-2 ${player.impnip === "N" ? "text-danger" : ""}`}>
                                        [{player.impnip}]
                                   </span>
                              )}
                         </div>

                         <div className="col-8 text-center" title="College Position and [GP]">
                              {cleanValue(player.colPos, "-")} [{cleanValue(player.genPos, "-")}]
                         </div>

                         <div className="col-6 text-center" title="College Jersey">
                              C# {cleanValue(player.jersey, "-")}
                         </div>

                         <div
                              className="col-20 text-start fw-bold text-truncate"
                              title={`${cleanValue(player.longSchool, "-")} [${cleanValue(player.school, "-")}]`}
                         >
                              {cleanValue(player.longSchool, "-")} [{cleanValue(player.school, "-")}]
                         </div>
                    </div>

                    <div className="col-48 d-flex align-items-center mb-2 pb-2 border-bottom">
                         <div className="col-16 px-1">
                              <button
                                   type="button"
                                   className={`btn btn-sm w-100 ${whatHDBox === "General" ? "btn-navy" : "btn-secondary text-light"}`}
                                   onClick={() => setWhatHDBox("General")}
                              >
                                   General
                              </button>
                         </div>

                         <div className="col-16 px-1">
                              <button
                                   type="button"
                                   className={`btn btn-sm w-100 ${whatHDBox === "HWS" ? "btn-navy" : "btn-secondary text-light"}`}
                                   onClick={() => setWhatHDBox("HWS")}
                              >
                                   HWS
                              </button>
                         </div>

                         <div className="col-16 px-1">
                              <button
                                   type="button"
                                   className={`btn btn-sm w-100 ${whatHDBox === "DraftScout" ? "btn-navy" : "btn-secondary text-light"}`}
                                   onClick={() => setWhatHDBox("DraftScout")}
                              >
                                   Draft Scout
                              </button>
                         </div>
                    </div>

                    {whatHDBox === "General" && (
                         <div id="generalBox" className="col-48">
                              <div id="leagueBoxes" className="col-48 mb-2">
                                   {isRealValue(player.cflTeam) && (
                                        <LeagueRow
                                             tone="alert-danger"
                                             label="CFL"
                                             team={player.cflTeam}
                                             status={player.cflStatus}
                                             date={player.cflDate}
                                             draftYear={player.cflDraftYear}
                                             draftTeam={player.cflDraftTeam}
                                             draftRound={player.cflDraftRound}
                                             draftRoundPick={player.cflDraftRoundPick}
                                             extraRight={`FCY: ${cleanValue(player.optionYr, "NA")}`}
                                        />
                                   )}

                                   {isRealValue(player.nflTeam) && (
                                        <LeagueRow
                                             tone="alert-info"
                                             label="NFL"
                                             team={player.nflTeam}
                                             status={player.nflStatus}
                                             date={player.nflDate}
                                             draftYear={player.nflDraftYear}
                                             draftTeam={player.nflDraftTeam}
                                             draftRound={player.nflDraftRound}
                                             draftRoundPick={player.nflDraftRoundPick}
                                        />
                                   )}

                                   {isRealValue(player.ufl24Team) && (
                                        <LeagueRow
                                             tone="alert-warning"
                                             label="UFL24"
                                             team={player.ufl24Team}
                                             status={player.ufl24Status}
                                             date={player.ufl24Date}
                                             draftYear={player.ufl24DraftYear}
                                             draftTeam={player.ufl24DraftTeam}
                                             draftRound={player.ufl24DraftRound}
                                             draftRoundPick={player.ufl24DraftRoundPick}
                                        />
                                   )}

                                   {isRealValue(player.theEvent) && (
                                        <div className="col-48 alert-purple d-flex align-items-center px-2 py-2 mb-1 rounded">
                                             <div className="col-12 d-flex align-items-center">
                                                  <div className="col-14 text-end pe-2 fw-bold">Event:</div>
                                                  <div className="col-14 text-truncate" title={cleanValue(player.theEvent, "-")}>
                                                       {cleanValue(player.theEvent, "-")}
                                                  </div>
                                             </div>

                                             <div className="col-4"></div>

                                             <div className="col-6 text-center" title="Event Date">
                                                  {cleanDate(player.theEventDate)}
                                             </div>

                                             <div className="col-4"></div>

                                             <div
                                                  className="col-6 text-center text-truncate"
                                                  title={cleanValue(player.theEventPos, "E-Pos")}
                                             >
                                                  {cleanValue(player.theEventPos, "NA")}
                                             </div>

                                             <div className="col-5 text-center" title="Event Side">
                                                  {cleanValue(player.theEventSide, "NA")}
                                             </div>

                                             <div className="col-5 text-center" title="Event Year">
                                                  {cleanValue(player.theEventYear, "NA")}
                                             </div>

                                             <div className="col-4"></div>
                                        </div>
                                   )}
                              </div>

                              <div className="col-48 d-flex align-items-center py-1 mb-2 border-bottom">
                                   <div className="col-8 text-end pe-2 fw-bold">Special Teams:</div>
                                   <div className="col-18 text-truncate" title={cleanValue(player.specialTeams, "No Special Teams")}>
                                        {cleanValue(player.specialTeams, "No Special Teams")}
                                   </div>

                                   <div className="col-6 text-end pe-2 fw-bold">CFL DY:</div>
                                   <div className="col-4">
                                        <div className="alert-danger text-center py-2 mb-0 rounded">
                                             {cleanValue(player.cflDraftYear, "-")}
                                        </div>
                                   </div>

                                   <div className="col-6 text-end pe-2 fw-bold">NFL DY:</div>
                                   <div className="col-4">
                                        <div className="alert-info text-center py-2 mb-0 rounded">
                                             {cleanValue(player.nflDraftYear, "-")}
                                        </div>
                                   </div>
                              </div>

                              <DataRow
                                   leftLabel="Height"
                                   leftValue={player.height}
                                   leftSub={player.heightV}
                                   leftDate={player.heightDate}
                                   rightLabel="Weight"
                                   rightValue={player.weight}
                                   rightSub={player.weightV}
                                   rightDate={player.weightDate}
                              />

                              <div className="col-48 d-flex align-items-center py-1 mb-1 border-bottom">
                                   <div className="col-6 text-end pe-2 fw-bold">Speed:</div>
                                   <div className="col-4">{cleanValue(player.speed40Fastest, "NA")}</div>
                                   <div className="col-3">{cleanValue(player.speed40FVerified, "-")}</div>
                                   <div className="col-7">{cleanDate(player.speed40FDate)}</div>

                                   <div className="col-6 text-end pe-2 fw-bold">Surface:</div>
                                   <div className="col-10 text-truncate" title={cleanValue(player.speed40FSurface, "-")}>
                                        {cleanValue(player.speed40FSurface, "-")}
                                   </div>

                                   <div className="col-6 text-end pe-2 fw-bold">Proj Rnd:</div>
                                   <div className="col-6">{cleanValue(player.projRnd, "NA")}</div>
                              </div>

                              <SplitRow
                                   leftLabel="Position Rank"
                                   leftValue={`${cleanValue(player.rankphp, "-")} of ${cleanValue(player.rateAll, "-")}`}
                                   midLabel="Pos"
                                   midValue={player.colPos}
                                   rightLabel="Jersey"
                                   rightValue={player.jersey}
                              />

                              <SplitRow
                                   leftLabel="College Team"
                                   leftValue={player.school}
                                   midLabel="Prev Team"
                                   midValue={player.lastSchool}
                                   rightLabel="Prev Team 2"
                                   rightValue={player.lastSchoolTwo}
                              />

                              <SplitRow
                                   leftLabel="Transfer Portal"
                                   leftValue={player.transferPortal || "Not"}
                                   midLabel="DOB"
                                   midValue={cleanDate(player.dob)}
                                   rightLabel="HS Stars"
                                   rightValue={player.hsStars}
                              />

                              <SplitRow
                                   leftLabel="Age"
                                   leftValue={player.age}
                                   midLabel="Home Town"
                                   midValue={player.homeTown}
                                   rightLabel="State"
                                   rightValue={player.homeState}
                              />

                              <SplitRow
                                   leftLabel="Country"
                                   leftValue={player.homeCountry}
                                   midLabel="Agent"
                                   midValue={player.agentName}
                                   rightLabel="Phone"
                                   rightValue={player.agentPhone1}
                              />

                              <div className="col-48 d-flex align-items-center py-1 mb-1">
                                   <div className="col-8 text-end pe-2 fw-bold">Email:</div>
                                   <div className="col-40">Coming Soon</div>
                              </div>
                         </div>
                    )}

                    {whatHDBox === "HWS" && <HWSEditShow currentPlayer={currentPlayer} />}
                    {whatHDBox === "DraftScout" && <HWSDraftScout />}
               </div>
          </DragBox2026>
     );
};

export default HDBox;